/**
 * Componente de resumo gerado por IA
 */

import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import GavelIcon from '@mui/icons-material/Gavel'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { AISummary } from '../../types/transcription'
import { getAISummary } from '../../services/api'
import { contextualErrors, FriendlyError } from '../../services/errorHandler'

type Props = {
  fileId: string
  onSummaryGenerated?: (summary: AISummary) => void
}

export default function AISummaryPanel({ fileId, onSummaryGenerated }: Props) {
  const [summary, setSummary] = useState<AISummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [expanded, setExpanded] = useState(true)

  const handleGenerate = async () => {
    setLoading(true)
    setError(null)

    try {
      // Chama API real do backend monkee-ai-api
      const result = await getAISummary(fileId)
      if (result === null) {
        setError({
          type: 'not_found',
          title: 'Resumo não disponível',
          message: 'O resumo ainda não está disponível. O arquivo está sendo processado.',
          suggestion: 'Aguarde alguns minutos e tente novamente.',
          canRetry: true
        })
        return
      }
      setSummary(result)
      onSummaryGenerated?.(result)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerate = () => {
    setSummary(null)
    handleGenerate()
  }

  // Estado inicial: botão para gerar
  if (!summary && !loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 40, color: '#1976d2' }} />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Resumo com Inteligência Artificial
          </Typography>

          <Typography variant="body2" sx={{ color: '#666', mb: 3, maxWidth: 400, mx: 'auto' }}>
            Gere automaticamente um resumo da transcrição, com pontos-chave, ações pendentes e decisões identificadas.
          </Typography>

          {error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 600, mb: 0.5 }}>
                {error.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#ef6c00' }}>
                {error.message}
              </Typography>
              {error.suggestion && (
                <Typography variant="caption" sx={{ color: '#f57c00', display: 'block', mt: 0.5 }}>
                  {error.suggestion}
                </Typography>
              )}
            </Box>
          )}

          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleGenerate}
            sx={{ borderRadius: 2 }}
          >
            Gerar Resumo com IA
          </Button>

          <Typography variant="caption" sx={{ display: 'block', color: '#999', mt: 2 }}>
            ⏱️ Tempo estimado: 5-10 segundos
          </Typography>
        </CardContent>
      </Card>
    )
  }

  // Estado de loading
  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Gerando resumo com IA...
            </Typography>
          </Box>

          <Stack spacing={2}>
            <Skeleton variant="text" width="100%" height={24} />
            <Skeleton variant="text" width="90%" height={24} />
            <Skeleton variant="text" width="95%" height={24} />
            <Skeleton variant="text" width="70%" height={24} />

            <Divider sx={{ my: 1 }} />

            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="rounded" width="100%" height={60} />

            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="rounded" width="100%" height={60} />
          </Stack>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              🤖 Analisando conteúdo e identificando informações relevantes...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  // Estado com resumo gerado
  return (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: '#1976d2' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Resumo com IA
            </Typography>
            <Chip
              label="Gerado automaticamente"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRegenerate}
              sx={{ textTransform: 'none' }}
            >
              Regenerar
            </Button>
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none' }}
            >
              {expanded ? 'Recolher' : 'Expandir'}
            </Button>
          </Box>
        </Box>

        <Collapse in={expanded}>
          {/* Resumo principal */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 2,
              mb: 3
            }}
          >
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {summary?.summary}
            </Typography>
          </Paper>

          {/* Pontos-chave */}
          {summary?.keyPoints && summary.keyPoints.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LightbulbIcon sx={{ color: '#ed6c02', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Pontos-chave
                </Typography>
              </Box>
              <List dense sx={{ bgcolor: '#fff8e1', borderRadius: 2 }}>
                {summary.keyPoints.map((point, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleOutlineIcon sx={{ color: '#ed6c02', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText primary={point} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Ações pendentes */}
          {summary?.actionItems && summary.actionItems.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TaskAltIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Ações Pendentes
                </Typography>
              </Box>
              <List dense sx={{ bgcolor: '#e8f5e9', borderRadius: 2 }}>
                {summary.actionItems.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          border: '2px solid #2e7d32',
                          borderRadius: 1
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Decisões */}
          {summary?.decisions && summary.decisions.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <GavelIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Decisões
                </Typography>
              </Box>
              <List dense sx={{ bgcolor: '#e3f2fd', borderRadius: 2 }}>
                {summary.decisions.map((decision, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <GavelIcon sx={{ color: '#1976d2', fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText primary={decision} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Collapse>

        {/* Timestamp */}
        <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 2, textAlign: 'right' }}>
          Gerado em: {summary?.generatedAt ? new Date(summary.generatedAt).toLocaleString('pt-BR') : '—'}
        </Typography>
      </CardContent>
    </Card>
  )
}
